import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Loader from '../../../utility/Loader';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    Input,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { GameCssStyles } from '../../../components/GameManagement/GameCssStyles';
import api_service from '../../../network/api_callbacks';
import { toast } from 'react-toastify';
import { generatePublicUrlForFileInAWSS3Bucket, getSingleFileByNameFromAWSS3Bucket, uploadFileToAWSS3Bucket } from '../../../services/uploadFileOnAWS_S3_Bucket';
import { checkMediaRatio, checkMediaRatio2, getMediaDimensionsAndRatio } from '../../../utility/Common';
import { setOnProgressCallback, uploadVideoToCloudflareStream } from '../../../network/cloudflare_stream_service/CloudflareStream_api_callback';
import ProgressModal from '../../../utility/ProgressModal';
import ReactPlayer from 'react-player';
import UploadProgressLoader from './UploadProgressLoader';

const getContentIdFromFileName = (name = '') => String(name).replace(/\.[^/.]+$/, '');

const resolvePublicUrl = (url, mediaType = '') => {
    if (!url) return '';
    if (mediaType === 'video' || String(url).includes('cloudflarestream.com') || String(url).includes('videodelivery.net')) {
        return url;
    }
    if (String(url).startsWith('http')) {
        return generatePublicUrlForFileInAWSS3Bucket(url) || url;
    }
    return generatePublicUrlForFileInAWSS3Bucket(url);
};
/** (W*H) */
const ratio = [
    {
        value: "440:75",
        label: "Home Sponserd Banner [6:1]"
    },
    {
        value: "200:160",
        label: "Home Game Banner [5:4]"
    },
    {
        value: "16:9",
        label: "Game Main Screen Poster [16:9]"
    },
    {
        value: "9:16",
        label: "Demo Video Screen [9:16]"
    },
    {
        value: "1:1",
        label: "Entity Square Image [1:1]"
    },
    {
        value: "400:100",
        label: "Contest Listing Banner [4:1]"
    },
    {
        value: "400:160",
        label: "Housie Waiting Screen Banner [5:2]"
    },
    {
        value: "400:140",
        label: "Leaderboard Banner [20:7]"
    },
]

function UploadImage(props) {

    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const state = location.state;
    const [loader, setLoader] = useState(false);

    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);

    const [selectedType, setSelectedType] = useState('');
    const [selectedRatio, setSelectedRatio] = useState(null);
    const [fileForUpload, setFileForUpload] = useState(null);
    const [singleUploadResult, setSingleUploadResult] = useState(null);

    const [folderUploading, setFolderUploading] = useState(false);
    const [totalFiles, setTotalFiles] = useState(0);
    const [uploadedFiles, setUploadedFiles] = useState(0);
    const [currentFileName, setCurrentFileName] = useState("");
    const [selectedFolderName, setSelectedFolderName] = useState("");
    const [currentProgress, setCurrentProgress] = useState(0);
    const workerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, []);

    const terminateWorker = () => {
        if (workerRef.current) {
            workerRef.current.terminate();
            workerRef.current = null;
        }
    };

    const generateCSVReport = (rows = []) => {
        const header = "Content ID,URL,Upload Status,Aspect Ratio\n";
        const csvRows = rows
            .map((row) => {
                const contentId = row?.contentId ?? row?.id ?? row?.["Content ID"] ?? "";
                const url = row?.url ?? row?.URL ?? "";
                const status = row?.status ?? row?.["Upload Status"] ?? "";
                const aspectRatio = row?.aspectRatio ?? row?.["Aspect Ratio"] ?? "";
                return `${contentId},${url},${status},${aspectRatio}`;
            })
            .join("\n");

        const blob = new Blob([header + csvRows], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const timestamp = new Date().toLocaleString("en-GB").replace(/[/:, ]/g, "_");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `Upload_Report_${timestamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const copyText = async (value, label = "Value") => {
        if (!value) {
            toast.error(`No ${label.toLowerCase()} to copy`);
            return;
        }
        try {
            await navigator.clipboard.writeText(String(value));
            toast.success(`${label} copied`);
        } catch {
            toast.error(`Could not copy ${label.toLowerCase()}`);
        }
    };

    const applySingleUploadSuccess = ({ file, url, aspectRatio = "", form }) => {
        const contentId = getContentIdFromFileName(file?.name);
        const publicUrl = resolvePublicUrl(url, form);
        const resultRow = {
            "Content ID": contentId,
            URL: publicUrl,
            "Upload Status": "Success",
            "Aspect Ratio": aspectRatio || "",
            form,
            fileName: file?.name || "",
        };
        setFileForUpload({
            form,
            url: publicUrl,
            aspectRatio: aspectRatio || "",
            contentId,
            status: "Success",
            fileName: file?.name || "",
        });
        setSingleUploadResult(resultRow);
        generateCSVReport([resultRow]);
        toast.success(`${form || "File"} uploaded successfully${file?.name ? `: ${file.name}` : ""}`);
    };

    const handleFileChange = async (e) => {
        console.log("handleFileChange called");
        const file = e.target.files[0];
        if (!file) return;
        if (file) {
            setSingleUploadResult(null);
            const mediaType = selectedType?.toLocaleLowerCase();
            const url = URL.createObjectURL(file);
            const mimeType = file.type;
            var type = "unknown";
            if (mimeType.startsWith(`${mediaType}/`)) {
                type = mediaType;
            }
            if (mediaType === "image") {
                try {
                    if (!selectedRatio) {
                        toast.error("Please select an aspect ratio before uploading images.");
                        return;
                    }

                    // Validate ratio on local file (CSV-style). Do not depend on PutObject response body / remote fetch.
                    const [w, h] = selectedRatio.split(":").map(Number);
                    const ratioObj = { [selectedRatio]: w / h };
                    const mediaInfo = await checkMediaRatio2(file, mediaType, 0.02, ratioObj);
                    if (!mediaInfo?.matched) {
                        toast.error(`Please select ${mediaType} in mentioned aspect ratio Width:${w} - Height:${h}`);
                        setFileForUpload(null);
                        setSingleUploadResult(null);
                        return;
                    }

                    setUploading(true);
                    setProgress(0);
                    // PutObject returns 200 with empty body — upload helper resolves URL via getUrl like CSV bulk.
                    const result = await uploadFileToAWSS3Bucket("banners", file, (percent) => setProgress(percent));
                    console.log("result : ", result);
                    const uploadedFileName = result?.fileName || result?.key?.split("/").pop();
                    const fileResult =
                        result?.url ||
                        (await getSingleFileByNameFromAWSS3Bucket("banners", uploadedFileName));
                    console.log("fileResult : ", fileResult);
                    if (!fileResult) {
                        throw new Error("Upload finished but public URL could not be resolved.");
                    }

                    applySingleUploadSuccess({
                        file: { ...file, name: uploadedFileName },
                        url: fileResult,
                        aspectRatio: mediaInfo?.ratio || selectedRatio || "",
                        form: mediaType,
                    });
                } catch (err) {
                    console.error("Image upload failed:", err);
                    toast.error(err?.message || `Upload failed for ${file?.name || "image"}`);
                    setFileForUpload(null)
                    setSingleUploadResult(null)
                } finally {
                    setUploading(false);
                    setProgress(0);
                }
            }
            else if (mediaType === "video") {
                try {
                    setUploading(true);
                    setProgress(0);
                    setOnProgressCallback((percent) => setProgress(percent));
                    const { width, height, mediaType, ratio } = await getMediaDimensionsAndRatio(file);
                    const mediaInfo = await checkMediaRatio(file, mediaType);
                    const resp = await uploadVideoToCloudflareStream(file);
                    const finalUrl = resp?.videoUrl;
                    console.log("Cloudflare Stream upload:", finalUrl);
                    console.log("mediaInfo : ", mediaInfo)
                    const videoObj = {
                        mediaType: "video",
                        url: `${finalUrl}`,
                        aspectRatio: `${ratio}`
                    }
                    
                    applySingleUploadSuccess({
                        file,
                        url: finalUrl,
                        aspectRatio: ratio || "",
                        form: mediaType,
                    });
                } catch (err) {
                    console.error("Upload failed:", err);
                    toast.error(err?.message || `Upload failed for ${file?.name || "file"}`);
                    setFileForUpload(null)
                    setSingleUploadResult(null)
                }
                finally {
                    setUploading(false);
                    setProgress(0);
                }
            }
            else if (mediaType === "audio") {
                try {
                    setUploading(true);
                    setProgress(0);
                    const result = await uploadFileToAWSS3Bucket("banners", file, (percent) => setProgress(percent));
                    console.log("result : ", result)
                    const uploadedFileName = result?.fileName || result?.key?.split('/').pop();
                    const fileResult =
                        result?.url ||
                        (await getSingleFileByNameFromAWSS3Bucket("banners", uploadedFileName));
                    console.log("fileResult : ", fileResult)
                    if (!fileResult) {
                        throw new Error("Upload finished but public URL could not be resolved.");
                    }
                    applySingleUploadSuccess({
                        file: { ...file, name: uploadedFileName },
                        url: fileResult,
                        aspectRatio: "",
                        form: mediaType,
                    });
                } catch (err) {
                    console.error("Upload failed:", err);
                    toast.error(err?.message || `Upload failed for ${file?.name || "audio"}`);
                    setFileForUpload(null)
                    setSingleUploadResult(null)
                }
                finally {
                    setUploading(false);
                    setProgress(0);
                }
            }
            else {
                console.error("Unsupported file type selected. Please select a valid file.");
                toast.error("Unsupported file type selected. Please select a valid file.");
                setFileForUpload(null)
            }
        }
        else {
            console.error("No file selected");
            setFileForUpload(null)
        }
        e.target.value = null; // reset input
        console.log("fileForUpload : ", fileForUpload)
    }

    const handleFolderChangeForImageAndAudio = async (e) => {
        console.log("handleFolderChangeForImageAndAudio called");
        const files = Array.from(e.target.files);

        if (!files.length) return;

        // The first file usually has the relative path like "FolderName/fileName.ext"
        const firstFile = files[0];

        // Use webkitRelativePath to get folder structure
        const folderName = firstFile.webkitRelativePath
            ? firstFile.webkitRelativePath.split("/")[0]
            : "";

        setSelectedFolderName(folderName);
        setFolderUploading(true);
        setTotalFiles(files.length);
        setUploadedFiles(0);

        const mediaType = selectedType?.toLowerCase();

        // Sort files based on numeric suffix (e.g. SHM000004 → 4)
        const sortedFiles = files.sort((a, b) => {
            const getNumber = (name) => {
                const match = name.match(/(\d+)$/); // extract trailing digits
                return match ? parseInt(match[1], 10) : 0;
            };

            return getNumber(a.name) - getNumber(b.name);
        });

        // Array to store CSV rows
        const csvData = [];

        // Keep device awake during long uploads (best-effort)
        let wakeLock = null;
        try {
            if (navigator?.wakeLock?.request) {
                wakeLock = await navigator.wakeLock.request('screen');
            }
        } catch (_) { }

        // Loop through each file and upload accordingly
        for (const file of sortedFiles) {
            setCurrentFileName(file.name);
            setCurrentProgress(0);

            let uploadedUrl = "";
            let status = "Failed";
            let aspectRatioValue = "1:1";

            console.log("selectedType handleFolderChangeForImageAndAudio : ",selectedType)
            console.log("mediaType handleFolderChangeForImageAndAudio : ",mediaType)
            try {
                if (selectedType === 'Image Bulk Upload') {
                    const result = await uploadFileToAWSS3Bucket("banners", file, (percent) => {
                        setCurrentProgress(percent);
                    });
                    uploadedUrl =
                        result?.url ||
                        (await getSingleFileByNameFromAWSS3Bucket(
                            "banners",
                            result?.fileName || result?.key?.split("/").pop()
                        ));
                    status = uploadedUrl ? "Success" : "Failed";
                } 
                else if (selectedType === 'Image' && mediaType === "image") {
                    const [w, h] = selectedRatio.split(":").map(Number);
                    const ratioObj = { [selectedRatio]: w / h };
                    // Local file ratio check (same as single upload) — not remote URL
                    const mediaInfo = await checkMediaRatio2(file, mediaType, 0.02, ratioObj);

                    if (!mediaInfo?.matched) {
                        toast.error(`Image ${file.name} does not match the aspect ratio ${selectedRatio}`);
                    } else {
                        const result = await uploadFileToAWSS3Bucket("banners", file, (percent) => {
                            setCurrentProgress(percent);
                        });
                        const fileResult =
                            result?.url ||
                            (await getSingleFileByNameFromAWSS3Bucket(
                                "banners",
                                result?.fileName || result?.key?.split("/").pop()
                            ));
                        uploadedUrl = fileResult;
                        status = fileResult ? "Success" : "Failed";
                        aspectRatioValue = mediaInfo?.ratio || selectedRatio || "";
                    }
                } 
                else if (mediaType === "video") {
                    setCurrentProgress(0);
                    setOnProgressCallback((percent) => setCurrentProgress(percent));
                    const { width, height, mediaType: vType, ratio } = await getMediaDimensionsAndRatio(file);
                    const mediaInfo = await checkMediaRatio(file, vType);
                    const resp = await uploadVideoToCloudflareStream(file);
                    const finalUrl = resp?.videoUrl;
                    //setFileForUpload((prev) => [...(prev || []), { form: "video", url: finalUrl, aspectRatio: ratio }]);
                    uploadedUrl = finalUrl;
                    status = "Success";
                } 
                else if (mediaType === "audio") {
                    const result = await uploadFileToAWSS3Bucket("banners", file, (percent) => {
                        setCurrentProgress(percent);
                    });
                    const fileResult =
                        result?.url ||
                        (await getSingleFileByNameFromAWSS3Bucket(
                            "banners",
                            result?.fileName || result?.key?.split("/").pop()
                        ));

                    //setFileForUpload((prev) => [...(prev || []), { form: "audio", url: fileResult, aspectRatio: "" }]);
                    uploadedUrl = fileResult;
                    status = fileResult ? "Success" : "Failed";
                } 
                else {
                    console.error(`Unsupported file type: ${file.name}`);
                    toast.error("Unsupported file type selected. Please select a valid file.");
                }
            } catch (err) {
                console.error(`Upload failed for ${file.name}:`, err);
                toast.error(err?.message || `Upload failed for ${file.name}`);
            } finally {
                const contentId = (
                    file.name.replace(/\.[^/.]+$/, "")
                );
                csvData.push({
                    "Content ID": contentId,
                    URL: uploadedUrl,
                    "Upload Status": status,
                    "Aspect Ratio": aspectRatioValue,
                });
                setUploadedFiles((prev) => prev + 1);
                setCurrentFileName("");
                setCurrentProgress(0);
            }
        }

        setFolderUploading(false);

        // Release wake lock
        try { await wakeLock?.release(); } catch (_) { }

        // Generate CSV after all files are processed
        const csvHeader = "Content ID,URL,Upload Status,Aspect Ratio\n";
        const csvRows = csvData.map(row => `${row["Content ID"]},${row.URL},${row["Upload Status"]},${row["Aspect Ratio"]}`).join("\n");
        const csvContent = csvHeader + csvRows;

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const timestamp = new Date().toLocaleString("en-GB").replace(/[/:, ]/g, "_");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `Upload_Report_${timestamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const successCount = csvData.filter((row) => row["Upload Status"] === "Success").length;
        const failCount = csvData.length - successCount;
        if (successCount > 0 && failCount === 0) {
            toast.success(`${successCount} file${successCount > 1 ? "s" : ""} uploaded successfully`);
        } else if (successCount > 0) {
            toast.info(`${successCount} uploaded, ${failCount} failed. Check the CSV report.`);
        } else {
            toast.error(`Upload finished with ${failCount} failure${failCount > 1 ? "s" : ""}. Check the CSV report.`);
        }

        e.target.value = null;
        console.log("fileForUpload:", fileForUpload);
    };

    const handleFolderChangeForVideo = async (e) => {
        console.log("handleFolderChangeForVideo called");
        const inputElement = e.target;
        const files = Array.from(inputElement.files || []);
        if (!files.length) return;

        if (selectedType === "Image" && !selectedRatio) {
            toast.error("Please select an aspect ratio before uploading images.");
            inputElement.value = null;
            return;
        }

        const firstFile = files[0];
        const folderName = firstFile.webkitRelativePath
            ? firstFile.webkitRelativePath.split("/")[0]
            : "";

        setSelectedFolderName(folderName);
        setFolderUploading(true);
        setTotalFiles(files.length);
        setUploadedFiles(0);
        setCurrentFileName("");
        setCurrentProgress(0);

        const mediaType = selectedType?.toLowerCase();
        const getNumber = (name) => {
            const match = name.match(/(\d+)$/);
            return match ? parseInt(match[1], 10) : 0;
        };
        const sortedFiles = files.sort((a, b) => getNumber(a.name) - getNumber(b.name));

        let wakeLock = null;
        const releaseWakeLock = async () => {
            if (wakeLock && wakeLock.release) {
                try {
                    await wakeLock.release();
                } catch (_) {
                    // ignore wake lock release errors
                } finally {
                    wakeLock = null;
                }
            }
        };

        if (navigator?.wakeLock?.request) {
            try {
                wakeLock = await navigator.wakeLock.request("screen");
            } catch (err) {
                console.warn("Wake lock request failed", err);
            }
        }

        terminateWorker();

        if (typeof Worker === "undefined") {
            toast.error("Web Workers are not supported in this browser.");
            setFolderUploading(false);
            await releaseWakeLock();
            inputElement.value = null;
            return;
        }

        const worker = new Worker(new URL('../../../workers/uploadWorker.js', import.meta.url));
        workerRef.current = worker;

        worker.onerror = async () => {
            toast.error("Upload worker encountered an unexpected error.");
            setFolderUploading(false);
            setCurrentFileName("");
            setCurrentProgress(0);
            await releaseWakeLock();
            terminateWorker();
            inputElement.value = null;
        };

        worker.onmessage = async (msg) => {
            const { type, ...payload } = msg.data || {};
            if (type === "progress") {
                if (payload.fileName) setCurrentFileName(payload.fileName);
                if (typeof payload.progress === "number") setCurrentProgress(payload.progress);
                if (payload.completed) {
                    setUploadedFiles((prev) => Math.min(prev + 1, sortedFiles.length));
                }
            } else if (type === "notification") {
                if (payload.message) {
                    if (payload.level === "error") {
                        toast.error(payload.message);
                    } else {
                        toast.info(payload.message);
                    }
                }
            } else if (type === "done") {
                generateCSVReport(payload.csvData || []);
                setFolderUploading(false);
                setCurrentFileName("");
                setCurrentProgress(0);
                await releaseWakeLock();
                terminateWorker();
                inputElement.value = null;
            } else if (type === "error") {
                toast.error(payload.message || "Upload failed.");
                setFolderUploading(false);
                setCurrentFileName("");
                setCurrentProgress(0);
                await releaseWakeLock();
                terminateWorker();
                inputElement.value = null;
            }
        };

        worker.postMessage({
            files: sortedFiles,
            mediaType,
            selectedType,
            selectedRatio,
        });
    };
    
    // const handleFolderChange = (e) => {
    //     const files = Array.from(e.target.files);
    //     if (!files.length) return;
    
    //     setFolderUploading(true);
    //     setTotalFiles(files.length);
    
    //     const worker = new Worker("/uploadWorker.js");
    
    //     worker.postMessage({
    //         files,
    //         mediaType: selectedType?.toLowerCase(),
    //         selectedType,
    //         selectedRatio
    //     });
    
    //     worker.onmessage = (msg) => {
    //         const data = msg.data;
    
    //         if (data.type === "progress") {
    //             setCurrentFileName(data.fileName);
    //             setCurrentProgress(data.progress);
    //             if (data.completed) setUploadedFiles((prev) => prev + 1);
    //         }
    
    //         if (data.type === "done") {
    //             generateCSV(data.csvData);
    //             setFolderUploading(false);
    //             worker.terminate();
    //         }
    //     };
    
    //     e.target.value = null;
    // };

    // const generateCSV = (csvData) => {
    //     const header = "Content ID,URL,Upload Status,Aspect Ratio\n";
    //     const rows = csvData
    //         .map((r) => `${r.id},${r.url},${r.status},${r.aspectRatio}`)
    //         .join("\n");
    
    //     const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    //     const link = document.createElement("a");
    //     const timestamp = new Date().toLocaleString("en-GB").replace(/[/:, ]/g, "_");
    
    //     link.href = URL.createObjectURL(blob);
    //     link.setAttribute("download", `Upload_Report_${timestamp}.csv`);
    //     document.body.appendChild(link);
    //     link.click();
    //     link.remove();
    // };

    return (
        <>
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    width: '100%',
                    paddingTop: '80px',
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    paddingBottom: '20px',

                }}>

                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    padding: 4,
                    position: "sticky",   // ✅ stick within parent container
                    top: 0,               // ✅ stick to the top
                    zIndex: 5,            // ✅ ensure it stays above content
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #ddd", // optional for visual separation
                }}>

                    <Box style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: "flex",
                        flex: 0,
                        flexDirection: "row",
                        gap: 2,
                    }}>
                        <Typography variant="h5" sx={{ width: 180, mt: 0, fontWeight: 700, textAlign: 'end' }}>Upload Multimedia</Typography>
                    </Box>


                    {/* {fileName && <Typography sx={{ mt: 0, ml: 10, textAlign: 'left' }}>Selected File: {fileName}</Typography>} */}

                    <Box style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: "flex",
                        flex: 0,
                        flexDirection: "row",
                        gap: 2,
                    }}>
                        <FormControl style={{ width: "240px" }}>
                            <InputLabel sx={GameCssStyles.inputLabelStyle_sx}>{"Select Multimedia Type"}</InputLabel>
                            <Select
                                value={selectedType || ''}
                                onChange={(e) => setSelectedType(e.target.value)}
                                label={"Select Multimedia Type"}
                                sx={GameCssStyles.formFiledStyle_sx2}
                            >
                                <MenuItem value="" disabled>{`Select Multimedia Type`}</MenuItem>
                                {[
                                    "Image",
                                    "Audio",
                                    "Video",
                                    "Image Bulk Upload"
                                ]?.map((item, idx) => (
                                    <MenuItem key={idx} value={item || ''} sx={GameCssStyles.menuItemStyle_sx}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedType === 'Image' && <FormControl style={{ width: "240px" }}>
                            <InputLabel sx={GameCssStyles.inputLabelStyle_sx}>{"Select Ratio"}</InputLabel>
                            <Select
                                value={selectedRatio || ''}
                                onChange={(e) => setSelectedRatio(e.target.value)}
                                label={"Select Ratio"}
                                sx={GameCssStyles.formFiledStyle_sx2}
                            >
                                <MenuItem value="" disabled>{`Select Ratio`}</MenuItem>
                                {ratio?.map((item, idx) => (
                                    <MenuItem key={idx} value={item?.value || ''} sx={GameCssStyles.menuItemStyle_sx}>
                                        {item?.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>}

                        {((selectedType === 'Audio') || (selectedType === 'Video') || (selectedType === 'Image' && selectedRatio !== null)) && <Button
                            variant="contained"
                            color="success"
                            component="label"   // 👈 makes the button act like a label for the input
                            sx={{ borderRadius: 2, textTransform: "none", ml: 5, mr: 0, height: 40, width: '150px' }}
                            onClick={() => {
                                console.log("selectedType onClick0 : ", selectedType?.toLocaleLowerCase())
                            }}>
                            Upload File
                            {/* Hidden file input */}
                            {(selectedType !== '') && <input
                                type="file"
                                accept={
                                    selectedType.toLowerCase() === 'image'
                                        ? 'image/*'
                                        : selectedType.toLowerCase() === 'video'
                                            ? 'video/*'
                                            : selectedType.toLowerCase() === 'audio'
                                                ? 'audio/*'
                                                : '*/*'
                                }
                                onChange={handleFileChange}
                                sx={{ display: 'none' }}
                                hidden
                            />}
                        </Button>}

                        {((selectedType === 'Audio') || (selectedType === 'Video') || (selectedType === 'Image' && selectedRatio !== null)) && <Button
                            variant="contained"
                            color="success"
                            component="label"   // 👈 makes the button act like a label for the input
                            sx={{ borderRadius: 2, textTransform: "none", ml: 5, mr: 0, height: 40, width: '150px' }}
                            onClick={() => {
                                console.log("selectedType onClick1 : ", selectedType?.toLocaleLowerCase())
                            }}>
                            Upload Folder
                            {/* Hidden file input */}
                            {(selectedType !== '') && <input
                                type="file"
                                webkitdirectory="true"   // ✅ enable folder selection (Chrome/Edge/Safari)
                                directory=""             // ✅ compatibility for Firefox
                                multiple                 // allow multiple files inside folder
                                accept={
                                    selectedType.toLowerCase() === 'image'
                                        ? 'image/*'
                                        : selectedType.toLowerCase() === 'video'
                                            ? 'video/*'
                                            : selectedType.toLowerCase() === 'audio'
                                                ? 'audio/*'
                                                : '*/*'
                                }
                                onChange={(e)=>{
                                    if(selectedType?.toLocaleLowerCase() === 'video'){
                                        handleFolderChangeForVideo(e)
                                    } 
                                    else {
                                        handleFolderChangeForImageAndAudio(e)
                                    }
                                }
                            }
                                sx={{ display: 'none' }}
                                hidden
                            />}
                        </Button>}

                        {(selectedType === 'Image Bulk Upload') && <Button
                            variant="contained"
                            color="success"
                            component="label"   // 👈 makes the button act like a label for the input
                            sx={{ borderRadius: 2, textTransform: "none", ml: 5, mr: 0, height: 40, width: '150px' }}
                            onClick={() => {
                                console.log("selectedType onClick2 : ", selectedType?.toLowerCase())
                            }}>
                            Upload Folder
                            {/* Hidden file input */}
                            {(selectedType !== '') && <input
                                type="file"
                                webkitdirectory="true"   // ✅ enable folder selection (Chrome/Edge/Safari)
                                directory=""             // ✅ compatibility for Firefox
                                multiple                 // allow multiple files inside folder
                                accept={ selectedType.toLowerCase() === 'Image Bulk Upload' ? 'image/*' : '*/*' }
                                onChange={(e)=>{
                                    if(selectedType?.toLocaleLowerCase() === 'video'){
                                        handleFolderChangeForVideo(e)
                                    } 
                                    else {
                                        handleFolderChangeForImageAndAudio(e)
                                    }
                                }}
                                sx={{ display: 'none' }}
                                hidden
                            />}
                        </Button>}
                    </Box>

                </Box>

                {/* <Box sx={{ display: 'flex', mt: 4, gap: 1, alignItems: 'center' }}>
                    <TextField
                        value={fileForUpload?.url || ''}
                        fullWidth
                        size="medium"
                        variant="outlined"
                        label="Paste Link"
                        InputProps={{
                            readOnly: false,
                        }}
                        onChange={(e) => setFileForUpload({ ...fileForUpload, url: e.target.value })}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (!fileForUpload) return;
                            navigator.clipboard.writeText(fileForUpload?.url);
                        }}
                        style={{ width: 150, height: 50 }}
                    >
                        {'Prview Link'}
                    </Button>
                </Box> */}

                <Box
                    sx={{
                        flex: 1,
                        height: '100%',
                        width: '100%',
                        mt: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0, // important for flex children to grow/shrink
                    }}>

                    {(fileForUpload || singleUploadResult) && (
                        <>
                            <Box sx={{ mt: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        Upload details
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ContentCopyIcon />}
                                            onClick={() => {
                                                const row = singleUploadResult || {
                                                    "Content ID": fileForUpload?.contentId || "",
                                                    URL: fileForUpload?.url || "",
                                                    "Upload Status": fileForUpload?.status || "Success",
                                                    "Aspect Ratio": fileForUpload?.aspectRatio || "",
                                                };
                                                const text = [
                                                    `Content ID: ${row["Content ID"] || ""}`,
                                                    `URL: ${row.URL || ""}`,
                                                    `Upload Status: ${row["Upload Status"] || ""}`,
                                                    `Aspect Ratio: ${row["Aspect Ratio"] || ""}`,
                                                ].join("\n");
                                                copyText(text, "Upload details");
                                            }}
                                        >
                                            Copy all
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<DownloadIcon />}
                                            onClick={() => {
                                                const row = singleUploadResult || {
                                                    "Content ID": fileForUpload?.contentId || "",
                                                    URL: fileForUpload?.url || "",
                                                    "Upload Status": fileForUpload?.status || "Success",
                                                    "Aspect Ratio": fileForUpload?.aspectRatio || "",
                                                };
                                                generateCSVReport([row]);
                                            }}
                                        >
                                            Download CSV
                                        </Button>
                                    </Box>
                                </Box>

                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Content ID</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>URL</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Upload Status</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Aspect Ratio</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    {singleUploadResult?.["Content ID"] || fileForUpload?.contentId || "—"}
                                                </TableCell>
                                                <TableCell sx={{ maxWidth: 420, wordBreak: "break-all" }}>
                                                    {singleUploadResult?.URL || fileForUpload?.url || "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {singleUploadResult?.["Upload Status"] || fileForUpload?.status || "Success"}
                                                </TableCell>
                                                <TableCell>
                                                    {singleUploadResult?.["Aspect Ratio"] || fileForUpload?.aspectRatio || "—"}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        title="Copy Content ID"
                                                        onClick={() =>
                                                            copyText(
                                                                singleUploadResult?.["Content ID"] || fileForUpload?.contentId,
                                                                "Content ID"
                                                            )
                                                        }
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        title="Copy URL"
                                                        onClick={() =>
                                                            copyText(
                                                                singleUploadResult?.URL || fileForUpload?.url,
                                                                "URL"
                                                            )
                                                        }
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Box sx={{ display: 'flex', mt: 2, gap: 1, alignItems: 'center' }}>
                                    <TextField
                                        value={fileForUpload?.url || singleUploadResult?.URL || ""}
                                        fullWidth
                                        size="medium"
                                        variant="outlined"
                                        label="File Link"
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() =>
                                            copyText(fileForUpload?.url || singleUploadResult?.URL, "URL")
                                        }
                                        style={{ width: 150, height: 50 }}
                                    >
                                        Copy Link
                                    </Button>
                                </Box>
                            </Box>

                            {(selectedType === 'Image' && fileForUpload !== null) && <Box sx={{ mt: 4 }}>
                                <Typography variant="h5" sx={{ mt: 0, mb: 2, fontWeight: 700, }}>Preview:</Typography>
                                <img
                                    src={fileForUpload?.url}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8 }}
                                />
                            </Box>}

                            {(selectedType === 'Audio' && fileForUpload !== null) && <Box sx={{ mt: 4 }}>
                                <Typography variant="h5" sx={{ mt: 0, mb: 2, fontWeight: 700, }}>Preview:</Typography>
                                <audio
                                    src={fileForUpload?.url}
                                    controls
                                    style={{
                                        width: "100%",
                                        height: "45px",
                                        pointerEvents: "auto", // allow interaction
                                        zIndex: 0,
                                    }}
                                />
                            </Box>}

                            {/* {(selectedType === 'Video' && fileForUpload !== null) && <Box sx={{ mt: 12 }}>
                                <Typography variant="h5" sx={{ mt: 0, mb: 2, fontWeight: 700, }}>Preview:</Typography>
                                {fileForUpload?.url?.includes("vimeo.com") ? (
                                    <iframe
                                        title="vimeo-player"
                                        src={`https://player.vimeo.com/video/${fileForUpload?.url?.split("/")?.pop()}?autoplay=1&muted=1&controls=1&loop=0`}
                                        width="100%"
                                        height="500px"
                                        style={{ border: 0, zIndex: 0 }}
                                        frameBorder="0"
                                        allow="autoplay; fullscreen"
                                    />
                                ) : (
                                    <video
                                        src={fileForUpload?.url}
                                        style={{
                                            width: "100%",
                                            height: "500px",
                                            objectFit: "cover",
                                            zIndex: 0,
                                        }}
                                        autoPlay
                                        muted
                                        loop
                                    />
                                )}
                            </Box>} */}
                        </>
                    )}

                </Box>
            </Box>

            {(uploading || loader) && <ProgressModal open={uploading || loader} isLoader={loader} progress={progress} title="Uploading File" />}
            {folderUploading && (
                <UploadProgressLoader
                    open={folderUploading}
                    folderName={selectedFolderName}
                    totalFiles={totalFiles}
                    uploadedFiles={uploadedFiles}
                    currentFileName={currentFileName}
                    currentFileProgress={currentProgress}
                    isUploading={false}
                    title={`Uploading Folder - ${selectedFolderName}`}
                />
            )}
        </>
    );
}

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(UploadImage);